import { useEffect, useRef, useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

type CampoDir = 'dirXml' | 'dirPdf' | 'dirXmlNfse' | 'dirPdfNfse' | 'dirXmlNfe55' | 'dirPdfNfe55'

interface ItemDir { nome: string; caminho: string }
interface RespostaDir { atual: string; pai?: string; itens: ItemDir[] }

export default function Configuracoes() {
  const { empresas } = useEmpresa()
  const [logoEmp, setLogoEmp] = useState(0) // 0 = logo padrão
  const [logoAtual, setLogoAtual] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [dirXml, setDirXml] = useState('')
  const [dirPdf, setDirPdf] = useState('')
  const [salvandoDirs, setSalvandoDirs] = useState(false)
  const [msgDirs, setMsgDirs] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null)

  const [dirXmlNfse, setDirXmlNfse] = useState('')
  const [dirPdfNfse, setDirPdfNfse] = useState('')
  const [salvandoDirsNfse, setSalvandoDirsNfse] = useState(false)
  const [msgDirsNfse, setMsgDirsNfse] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null)

  const [dirXmlNfe55, setDirXmlNfe55] = useState('')
  const [dirPdfNfe55, setDirPdfNfe55] = useState('')
  const [salvandoDirsNfe55, setSalvandoDirsNfe55] = useState(false)
  const [msgDirsNfe55, setMsgDirsNfe55] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null)

  // ── Browser de diretórios ─────────────────────────────────────────────────
  const [browserAberto, setBrowserAberto] = useState(false)
  const [browserDestino, setBrowserDestino] = useState<CampoDir>('dirXml')
  const [browserAtual, setBrowserAtual] = useState('')
  const [browserPai, setBrowserPai] = useState('')
  const [browserItens, setBrowserItens] = useState<ItemDir[]>([])
  const [browserCarregando, setBrowserCarregando] = useState(false)
  const [browserErro, setBrowserErro] = useState('')

  async function navegarPara(caminho: string) {
    setBrowserCarregando(true)
    setBrowserErro('')
    try {
      const r = await api.get<RespostaDir>('/config/listar-dir', {
        params: { path: caminho },
      })
      setBrowserAtual(r.data.atual)
      setBrowserPai(r.data.pai ?? '')
      setBrowserItens(r.data.itens)
    } catch (err: any) {
      setBrowserErro(err?.response?.data?.error ?? 'Erro ao listar diretório.')
    } finally {
      setBrowserCarregando(false)
    }
  }

  function abrirBrowser(destino: CampoDir, caminhoAtual: string) {
    setBrowserDestino(destino)
    setBrowserAberto(true)
    navegarPara(caminhoAtual)
  }

  function selecionarPasta() {
    const destinos: Record<CampoDir, (v: string) => void> = {
      dirXml:       v => { setDirXml(v);       setMsgDirs(null) },
      dirPdf:       v => { setDirPdf(v);       setMsgDirs(null) },
      dirXmlNfse:   v => { setDirXmlNfse(v);   setMsgDirsNfse(null) },
      dirPdfNfse:   v => { setDirPdfNfse(v);   setMsgDirsNfse(null) },
      dirXmlNfe55:  v => { setDirXmlNfe55(v);  setMsgDirsNfe55(null) },
      dirPdfNfe55:  v => { setDirPdfNfe55(v);  setMsgDirsNfe55(null) },
    }
    destinos[browserDestino](browserAtual)
    setBrowserAberto(false)
  }

  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    api.get('/config/diretorios').then(r => { setDirXml(r.data.dirXml ?? ''); setDirPdf(r.data.dirPdf ?? '') }).catch(() => {})
    api.get('/config/diretorios-nfse').then(r => { setDirXmlNfse(r.data.dirXml ?? ''); setDirPdfNfse(r.data.dirPdf ?? '') }).catch(() => {})
    api.get('/config/diretorios-nfe55').then(r => { setDirXmlNfe55(r.data.dirXml ?? ''); setDirPdfNfe55(r.data.dirPdf ?? '') }).catch(() => {})
  }, [])

  useEffect(() => {
    setLogoAtual(null)
    api.get('/config/logo', { params: { empcod: logoEmp || undefined } }).then(r => setLogoAtual(r.data.logo)).catch(() => {})
  }, [logoEmp])

  function handleArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    setArquivo(f)
    setPreview(URL.createObjectURL(f))
    setMsg(null)
  }

  async function handleSalvar() {
    if (!arquivo) return
    setSalvando(true)
    setMsg(null)
    try {
      const form = new FormData()
      form.append('logo', arquivo)
      const r = await api.post('/config/logo', form, {
        params: { empcod: logoEmp || undefined },
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setLogoAtual(r.data.logo)
      setPreview(null)
      setArquivo(null)
      if (inputRef.current) inputRef.current.value = ''
      setMsg({ tipo: 'ok', texto: 'Logo salvo com sucesso!' })
    } catch (err: any) {
      setMsg({ tipo: 'erro', texto: err?.response?.data?.error ?? 'Erro ao salvar logo.' })
    } finally {
      setSalvando(false)
    }
  }

  async function handleRemover() {
    const quem = logoEmp ? 'desta empresa (ela volta a usar o logo padrão)' : 'padrão (empresas sem logo próprio ficam sem logo)'
    if (!window.confirm(`Remover o logo ${quem}?`)) return
    setSalvando(true)
    setMsg(null)
    try {
      await api.delete('/config/logo', { params: { empcod: logoEmp || undefined } })
      setLogoAtual(null)
      setMsg({ tipo: 'ok', texto: 'Logo removido.' })
    } catch (err: any) {
      setMsg({ tipo: 'erro', texto: err?.response?.data?.error ?? 'Erro ao remover logo.' })
    } finally {
      setSalvando(false)
    }
  }

  function handleCancelar() {
    setArquivo(null)
    setPreview(null)
    setMsg(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const inputStyle = {
    flex: 1, boxSizing: 'border-box' as const,
    border: '1px solid #d1d5db', borderRadius: 4,
    padding: '6px 10px', fontSize: '0.82rem',
  }

  const btnBrowse = {
    padding: '6px 10px', background: '#f3f4f6',
    border: '1px solid #d1d5db', borderRadius: 4,
    fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap' as const,
    display: 'flex', alignItems: 'center', gap: 4,
  }

  function DirField({ label, value, onChange, campo }: {
    label: string; value: string; onChange: (v: string) => void; campo: CampoDir
  }) {
    return (
      <div>
        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
          {label}
        </label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="Ex: C:\ERP\xml\"
            style={inputStyle}
          />
          <button style={btnBrowse} onClick={() => abrirBrowser(campo, value)} title="Navegar pasta">
            📁 Navegar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ maxWidth: 560 }}>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.2rem' }}>
        Configurações
      </h2>

      <section>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>
          Logo da Empresa
        </h3>
        <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
          DACTE OS e DANFE usam o logo da empresa emitente; os relatórios usam o padrão. Formatos aceitos: PNG, JPG, GIF, WebP — máx. 2 MB.
          Empresa sem logo próprio usa o padrão.
        </p>

        <select
          value={logoEmp}
          onChange={e => { handleCancelar(); setLogoEmp(Number(e.target.value)) }}
          style={{ border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 10px', fontSize: '0.82rem', marginBottom: '0.75rem', maxWidth: '100%' }}
        >
          <option value={0}>Padrão (todas as empresas)</option>
          {empresas.map(e => (
            <option key={e.empcod} value={e.empcod}>{e.empcod} - {(e.empfan || e.empnom).trim()}</option>
          ))}
        </select>

        <div style={{
          width: 180, height: 80, border: '1px dashed #d1d5db', borderRadius: 6,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: '1rem', background: '#f9fafb', overflow: 'hidden',
        }}>
          {(preview || logoAtual) ? (
            <img
              src={preview ?? logoAtual!}
              alt="Logo"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>Sem logo</span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{
            display: 'inline-block', padding: '0.4rem 0.9rem',
            background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6,
            fontSize: '0.82rem', cursor: 'pointer', fontWeight: 500,
          }}>
            Selecionar arquivo
            <input
              ref={inputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.gif,.webp"
              onChange={handleArquivo}
              style={{ display: 'none' }}
            />
          </label>

          {arquivo && (
            <>
              <button
                onClick={handleSalvar}
                disabled={salvando}
                style={{
                  padding: '0.4rem 0.9rem', background: '#2563eb', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: '0.82rem',
                  cursor: salvando ? 'not-allowed' : 'pointer', fontWeight: 500,
                  opacity: salvando ? 0.7 : 1,
                }}
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                onClick={handleCancelar}
                disabled={salvando}
                style={{
                  padding: '0.4rem 0.9rem', background: '#fff', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.82rem',
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
            </>
          )}

          {!arquivo && logoAtual && (
            <button
              onClick={handleRemover}
              disabled={salvando}
              style={{
                padding: '0.4rem 0.9rem', background: '#fff', color: '#dc2626',
                border: '1px solid #fca5a5', borderRadius: 6, fontSize: '0.82rem',
                cursor: salvando ? 'not-allowed' : 'pointer',
              }}
            >
              Remover logo
            </button>
          )}
        </div>

        {arquivo && (
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.4rem' }}>
            {arquivo.name}
          </p>
        )}

        {msg && (
          <p style={{
            marginTop: '0.75rem', fontSize: '0.82rem',
            color: msg.tipo === 'ok' ? '#15803d' : '#dc2626',
          }}>
            {msg.texto}
          </p>
        )}
      </section>

      <hr style={{ margin: '1.5rem 0', borderColor: '#e5e7eb' }} />

      <section>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
          Diretórios CT-e OS
        </h3>
        <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
          Após autorização, o XML e o PDF serão gravados automaticamente nestes diretórios (tabela PARNFE).
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
          <DirField
            label="Diretório XML (nfedirret)"
            value={dirXml}
            onChange={v => { setDirXml(v); setMsgDirs(null) }}
            campo="dirXml"
          />
          <DirField
            label="Diretório PDF (nfedirdan)"
            value={dirPdf}
            onChange={v => { setDirPdf(v); setMsgDirs(null) }}
            campo="dirPdf"
          />
        </div>

        <button
          onClick={async () => {
            setSalvandoDirs(true)
            setMsgDirs(null)
            try {
              await api.post('/config/diretorios', { dirXml, dirPdf })
              setMsgDirs({ tipo: 'ok', texto: 'Diretórios salvos com sucesso!' })
            } catch (err: any) {
              setMsgDirs({ tipo: 'erro', texto: err?.response?.data?.error ?? 'Erro ao salvar.' })
            } finally {
              setSalvandoDirs(false)
            }
          }}
          disabled={salvandoDirs}
          style={{
            padding: '0.4rem 0.9rem', background: '#2563eb', color: '#fff',
            border: 'none', borderRadius: 6, fontSize: '0.82rem',
            cursor: salvandoDirs ? 'not-allowed' : 'pointer', fontWeight: 500,
            opacity: salvandoDirs ? 0.7 : 1,
          }}
        >
          {salvandoDirs ? 'Salvando...' : 'Salvar diretórios'}
        </button>

        {msgDirs && (
          <p style={{
            marginTop: '0.75rem', fontSize: '0.82rem',
            color: msgDirs.tipo === 'ok' ? '#15803d' : '#dc2626',
          }}>
            {msgDirs.texto}
          </p>
        )}
      </section>

      <hr style={{ margin: '1.5rem 0', borderColor: '#e5e7eb' }} />

      <section>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
          Diretórios NFS-e Nacional (Nota Municipal)
        </h3>
        <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
          Após autorização, o XML será gravado automaticamente neste diretório (tabela PARNFE — tipo M).
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
          <DirField
            label="Diretório XML (nfedirret)"
            value={dirXmlNfse}
            onChange={v => { setDirXmlNfse(v); setMsgDirsNfse(null) }}
            campo="dirXmlNfse"
          />
          <DirField
            label="Diretório DANFE (nfedirdan)"
            value={dirPdfNfse}
            onChange={v => { setDirPdfNfse(v); setMsgDirsNfse(null) }}
            campo="dirPdfNfse"
          />
        </div>

        <button
          onClick={async () => {
            setSalvandoDirsNfse(true)
            setMsgDirsNfse(null)
            try {
              await api.post('/config/diretorios-nfse', { dirXml: dirXmlNfse, dirPdf: dirPdfNfse })
              setMsgDirsNfse({ tipo: 'ok', texto: 'Diretórios salvos com sucesso!' })
            } catch (err: any) {
              setMsgDirsNfse({ tipo: 'erro', texto: err?.response?.data?.error ?? 'Erro ao salvar.' })
            } finally {
              setSalvandoDirsNfse(false)
            }
          }}
          disabled={salvandoDirsNfse}
          style={{
            padding: '0.4rem 0.9rem', background: '#2563eb', color: '#fff',
            border: 'none', borderRadius: 6, fontSize: '0.82rem',
            cursor: salvandoDirsNfse ? 'not-allowed' : 'pointer', fontWeight: 500,
            opacity: salvandoDirsNfse ? 0.7 : 1,
          }}
        >
          {salvandoDirsNfse ? 'Salvando...' : 'Salvar diretórios'}
        </button>

        {msgDirsNfse && (
          <p style={{
            marginTop: '0.75rem', fontSize: '0.82rem',
            color: msgDirsNfse.tipo === 'ok' ? '#15803d' : '#dc2626',
          }}>
            {msgDirsNfse.texto}
          </p>
        )}
      </section>

      <hr style={{ margin: '1.5rem 0', borderColor: '#e5e7eb' }} />

      <section>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>
          Diretórios NF-e Modelo 55
        </h3>
        <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
          Após autorização, o XML e o DANFE serão gravados automaticamente nestes diretórios (tabela PARNFE — tipo N).
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
          <DirField
            label="Diretório XML (nfedirret)"
            value={dirXmlNfe55}
            onChange={v => { setDirXmlNfe55(v); setMsgDirsNfe55(null) }}
            campo="dirXmlNfe55"
          />
          <DirField
            label="Diretório DANFE (nfedirdan)"
            value={dirPdfNfe55}
            onChange={v => { setDirPdfNfe55(v); setMsgDirsNfe55(null) }}
            campo="dirPdfNfe55"
          />
        </div>

        <button
          onClick={async () => {
            setSalvandoDirsNfe55(true)
            setMsgDirsNfe55(null)
            try {
              await api.post('/config/diretorios-nfe55', { dirXml: dirXmlNfe55, dirPdf: dirPdfNfe55 })
              setMsgDirsNfe55({ tipo: 'ok', texto: 'Diretórios salvos com sucesso!' })
            } catch (err: any) {
              setMsgDirsNfe55({ tipo: 'erro', texto: err?.response?.data?.error ?? 'Erro ao salvar.' })
            } finally {
              setSalvandoDirsNfe55(false)
            }
          }}
          disabled={salvandoDirsNfe55}
          style={{
            padding: '0.4rem 0.9rem', background: '#2563eb', color: '#fff',
            border: 'none', borderRadius: 6, fontSize: '0.82rem',
            cursor: salvandoDirsNfe55 ? 'not-allowed' : 'pointer', fontWeight: 500,
            opacity: salvandoDirsNfe55 ? 0.7 : 1,
          }}
        >
          {salvandoDirsNfe55 ? 'Salvando...' : 'Salvar diretórios'}
        </button>

        {msgDirsNfe55 && (
          <p style={{
            marginTop: '0.75rem', fontSize: '0.82rem',
            color: msgDirsNfe55.tipo === 'ok' ? '#15803d' : '#dc2626',
          }}>
            {msgDirsNfe55.texto}
          </p>
        )}
      </section>

      {/* ── Modal navegador de diretórios ───────────────────────────────────── */}
      {browserAberto && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => setBrowserAberto(false)}>
          <div style={{
            background: '#fff', borderRadius: 8, width: 560, maxWidth: '95vw',
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }} onClick={e => e.stopPropagation()}>

            {/* Cabeçalho */}
            <div style={{
              padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: '1rem' }}>📁</span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem', flex: 1 }}>
                Navegar diretórios
              </span>
              <button
                onClick={() => setBrowserAberto(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#6b7280' }}
              >
                ✕
              </button>
            </div>

            {/* Caminho atual */}
            <div style={{
              padding: '10px 18px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {browserPai && (
                <button
                  onClick={() => navegarPara(browserPai)}
                  style={{
                    padding: '3px 8px', background: '#fff', border: '1px solid #d1d5db',
                    borderRadius: 4, fontSize: '0.78rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}
                  title="Pasta acima"
                >
                  ↑ Acima
                </button>
              )}
              <span style={{
                fontSize: '0.78rem', color: '#374151', fontFamily: 'monospace',
                background: '#e5e7eb', padding: '3px 8px', borderRadius: 4,
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {browserAtual || 'Raiz (drives disponíveis)'}
              </span>
            </div>

            {/* Lista de pastas */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
              {browserCarregando ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem', fontSize: '0.85rem' }}>
                  Carregando...
                </p>
              ) : browserErro ? (
                <p style={{ textAlign: 'center', color: '#dc2626', padding: '2rem', fontSize: '0.82rem' }}>
                  {browserErro}
                </p>
              ) : browserItens.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem', fontSize: '0.82rem' }}>
                  Nenhuma subpasta encontrada.
                </p>
              ) : (
                browserItens.map(item => (
                  <div
                    key={item.caminho}
                    onClick={() => navegarPara(item.caminho)}
                    style={{
                      padding: '8px 18px', cursor: 'pointer', fontSize: '0.85rem',
                      display: 'flex', alignItems: 'center', gap: 8,
                      borderBottom: '1px solid #f3f4f6',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{ fontSize: '0.9rem' }}>📁</span>
                    <span style={{ color: '#1d4ed8', fontFamily: 'monospace' }}>{item.nome}</span>
                  </div>
                ))
              )}
            </div>

            {/* Rodapé */}
            <div style={{
              padding: '12px 18px', borderTop: '1px solid #e5e7eb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: '0.75rem', color: '#6b7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {browserAtual ? `Selecionado: ${browserAtual}` : 'Navegue até a pasta desejada'}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setBrowserAberto(false)}
                  style={{
                    padding: '6px 14px', background: '#fff', border: '1px solid #d1d5db',
                    borderRadius: 6, fontSize: '0.82rem', cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={selecionarPasta}
                  disabled={!browserAtual}
                  style={{
                    padding: '6px 14px', background: browserAtual ? '#2563eb' : '#93c5fd',
                    color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.82rem',
                    cursor: browserAtual ? 'pointer' : 'not-allowed', fontWeight: 600,
                  }}
                >
                  Selecionar esta pasta
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
